/**
 * Username input with the four async states the anchor draws: checking,
 * available, taken (with suggestions), not allowed. Shared by the signup
 * form and the identity modal so both behave identically.
 *
 * Suggestions appear only on a collision and are derived from what was typed.
 * Offering names before one is refused reads as us naming the player.
 */
import {ReactElement, useEffect} from "react";
import {useGetUsernameAvailability, UsernameCheckState} from "@/hooks/useGetUsernameAvailability";

interface Props {
    id: string;
    value: string;
    onChange: (next: string) => void;
    /** Reports the current check state up so the parent can gate submit. */
    onStateChange?: (state: UsernameCheckState) => void;
    hint?: string;
    className?: string;
    disabled?: boolean;
}

export function UsernameField({id, value, onChange, onStateChange, hint, className, disabled}: Props): ReactElement {
    const state = useGetUsernameAvailability(value);
    /* Reported after render, never during it: the parent stores this in state. */
    useEffect(() => {
        onStateChange?.(state);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const invalid = state.status === "invalid" || state.status === "taken" || state.status === "notAllowed";
    const status = (() => {
        switch (state.status) {
            case "checking":
                return <span className="username-field__status username-field__status--checking">Checking availability…</span>;
            case "available":
                return <span className="username-field__status username-field__status--ok">✓ {value.trim()} is available</span>;
            case "taken":
                return (
                    <>
                        <span className="username-field__status username-field__status--err">{value.trim()} is taken.</span>
                        {state.suggestions.length > 0 &&
                            <span className="username-field__suggestions">
                                {state.suggestions.map(s =>
                                    <button key={s} type="button" className="username-field__suggestion" onClick={() => onChange(s)}>
                                        {s}
                                    </button>,
                                )}
                            </span>}
                    </>
                );
            case "notAllowed":
                return <span className="username-field__status username-field__status--err">That username isn&rsquo;t available. Try another.</span>;
            case "invalid":
                return <span className="username-field__status username-field__status--err">{state.message}</span>;
            default:
                return hint ? <span className="username-field__status username-field__status--hint">{hint}</span> : null;
        }
    })();

    return (
        <div className={className}>
            <label htmlFor={id}>Username</label>
            <input
                id={id}
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={20}
                placeholder="Pick a username"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                aria-invalid={invalid}
                aria-describedby={`${id}-status`}
                className={state.status === "available" ? "is-available" : invalid ? "is-invalid" : undefined}
            />
            <span id={`${id}-status`} aria-live="polite">{status}</span>
        </div>
    );
}

export default UsernameField;
